import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
} from "react-beautiful-dnd";
import {
  MinusIcon,
  PlusIcon,
  LinkIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
  ChartBarIcon,
} from "@heroicons/react/16/solid";
import { LessonI, PageI } from "../../../types";
import Button from "../../../componentLibrary/Button";
import {
  useGetLesson,
  useGetTopic,
  useGetTopics,
  useGetPages,
  useGetPageFeedbackCount,
  useGetLessonFeedbackCount,
} from "../../../api/queries";
import Combobox from "../../../componentLibrary/Combobox";
import Card from "../../../componentLibrary/Card";
import Spinner from "../../../componentLibrary/Spinner";
import PageFeedbackModal from "../../../components/PageFeedbackModal";
import LessonFeedbackModal from "../../../components/LessonFeedbackModal";

interface PageRowProps {
  page: PageI;
  index: number;
  lessonId: string;
  currentPageId: string;
  setCurrentPageId: (pageId: string) => void;
  removePage: (pageId: string) => void;
  handleOpenFeedbackModal: (
    pageId: string,
    pageIndex: number,
    pageTitle: string,
  ) => void;
  provided: DraggableProvided;
}

const PageRow: React.FC<PageRowProps> = ({
  page,
  index,
  lessonId,
  currentPageId,
  setCurrentPageId,
  removePage,
  handleOpenFeedbackModal,
  provided,
}) => {
  const { data: feedbackCounts } = useGetPageFeedbackCount(lessonId, index + 1);
  const total = feedbackCounts?.total ?? 0;
  const unresolved = feedbackCounts?.unresolved ?? 0;
  const hasFeedback = total > 0;
  const allResolved = hasFeedback && unresolved === 0;

  const feedbackTitle = !hasFeedback
    ? "No feedback yet"
    : allResolved
      ? `All feedback resolved (${total} total). Open to review.`
      : `View feedback for this page (${unresolved} unresolved of ${total})`;

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{
        ...provided.draggableProps.style,
        userSelect: "none",
      }}
    >
      <div
        className={`flex justify-between py-2 px-4 items-center min-h-[42px]
          ${page._id === currentPageId ? "bg-gray-100" : "bg-white"}`}
      >
        <div className="flex flex-row items-center space-x-2">
          <Button
            variant="alternative"
            size="sm"
            onClick={() =>
              handleOpenFeedbackModal(
                page._id,
                index + 1,
                page.title || `[Page ${index + 1}]`,
              )
            }
            title={feedbackTitle}
            className={!hasFeedback ? "opacity-40 cursor-not-allowed" : ""}
            disabled={!hasFeedback}
          >
            <ChartBarIcon
              className={`w-5 h-5 ${
                !hasFeedback
                  ? "text-gray"
                  : allResolved
                    ? "text-electrum-200"
                    : "text-electrum"
              }`}
            />
          </Button>
          <span className="font-semibold capitalize py-1 break-words">
            {page.title || `[Page ${index + 1}]`}
          </span>
        </div>
        <div className="flex flex-row space-x-1">
          {page._id !== currentPageId && (
            <Button
              variant="alternative"
              size="sm"
              onClick={() => setCurrentPageId(page._id)}
            >
              Edit
            </Button>
          )}
          <Button
            variant="alternative"
            size="sm"
            onClick={() => removePage(page._id)}
            title="Remove from lesson"
          >
            <MinusIcon color="black" className="w-6 h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const PageList = ({
  lesson,
  setLesson,
  currentPageId,
  setCurrentPageId,
}: {
  lesson: LessonI;
  setLesson: React.Dispatch<React.SetStateAction<LessonI>>;
  currentPageId: string;
  setCurrentPageId: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { data: pages = [], isError, isLoading } = useGetPages();

  const addNewPage = () => {
    const newPageId = `page_${Date.now()}`;
    const newPages = [
      ...lesson.pages,
      { _id: newPageId, title: "", components: [] },
    ];
    setLesson({ ...lesson, pages: newPages });
  };

  const handleLinkPage = () => {
    if (!pageId) return;

    const pageExists = lesson.pages.some((page) => page._id === pageId);

    if (!pageExists) {
      const page = pages.find((page) => page._id === pageId);
      if (page) {
        setLesson((prevLesson) => ({
          ...prevLesson,
          pages: [...prevLesson.pages, page],
        }));
        setPageId("");
      } else {
        alert("Page not found.");
      }
    } else {
      alert("This page has already been added.");
    }
  };

  const handleCopyPage = () => {
    if (!pageId) return;

    const pageToCopy = pages.find((page) => page._id === pageId);

    if (pageToCopy) {
      const newPageId = `page_${Date.now()}`;
      const copiedPage = {
        ...pageToCopy,
        _id: newPageId,
      };

      setLesson((prevLesson) => ({
        ...prevLesson,
        pages: [...prevLesson.pages, copiedPage],
      }));

      setPageId("");
    } else {
      alert("Page not found.");
    }
  };

  const removePage = (pageId: string) => {
    const isConfirmed = confirm("Remove this page?");
    if (isConfirmed) {
      if (lesson.pages.length === 1) {
        const newPageId = `page_${Date.now()}`;
        setLesson({
          ...lesson,
          pages: [{ _id: newPageId, title: "", components: [] }],
        });
        setCurrentPageId(newPageId);
        return;
      }

      const newPages = lesson.pages.filter((page) => page._id !== pageId);
      setLesson({ ...lesson, pages: newPages });

      if (currentPageId === pageId) {
        setCurrentPageId(newPages[0]._id);
      }
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const newPages = Array.from(lesson.pages);
    const [removed] = newPages.splice(source.index, 1);
    newPages.splice(destination.index, 0, removed);

    setLesson({ ...lesson, pages: newPages });
  };

  const currentPage = lesson.pages.find((page) => page._id === currentPageId);
  const currentPageIndex = lesson.pages.findIndex(
    (page) => page._id === currentPageId,
  );
  const pageTitle = currentPage?.title || `Page ${currentPageIndex + 1}`;

  const [lessonId, setLessonId] = useState(null);
  const [moduleId, setModuleId] = useState(null);
  const [pageId, setPageId] = useState(null);

  const [lessonOptions, setLessonOptions] = useState<LessonI[]>([]);
  const [pageOptions, setPageOptions] = useState<PageI[]>(null);

  // Feedback modal state
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedPageForFeedback, setSelectedPageForFeedback] = useState<{
    pageId: string;
    pageIndex: number;
    pageTitle: string;
  } | null>(null);
  const [isLessonFeedbackModalOpen, setIsLessonFeedbackModalOpen] =
    useState(false);

  const { data: lessonFeedbackCounts } = useGetLessonFeedbackCount(lesson._id);
  const lessonFeedbackTotal = lessonFeedbackCounts?.total ?? 0;
  const lessonFeedbackUnresolved = lessonFeedbackCounts?.unresolved ?? 0;
  const hasLessonFeedback = lessonFeedbackTotal > 0;
  const allLessonFeedbackResolved =
    hasLessonFeedback && lessonFeedbackUnresolved === 0;
  const lessonFeedbackTitle = !hasLessonFeedback
    ? "No lesson feedback yet"
    : allLessonFeedbackResolved
      ? `All lesson feedback resolved (${lessonFeedbackTotal} total). Open to review.`
      : `All lesson feedback (${lessonFeedbackUnresolved} unresolved of ${lessonFeedbackTotal})`;

  const {
    data: modules,
    isError: isErrorModules,
    isLoading: isLoadingModules,
  } = useGetTopics();

  const { data: lessonInformation } = useGetLesson(lessonId);
  const { data: moduleInformation } = useGetTopic(moduleId);

  useEffect(() => {
    if (lessonInformation) {
      setPageOptions(lessonInformation.pages);
      if (!lessonInformation?.pages?.find((page) => page._id === pageId)) {
        setPageId(null);
      }
    } else {
      setPageOptions(null);
      setPageId(null);
    }
  }, [lessonInformation]);

  useEffect(() => {
    if (moduleInformation && !Array.isArray(moduleInformation)) {
      setLessonOptions(moduleInformation.lessons);
      const pagesList = moduleInformation.lessons.flatMap(
        (lesson) => lesson.pages,
      );
      setPageOptions(pagesList);
    } else {
      setLessonOptions([]);
      setPageOptions(null);
    }
  }, [moduleInformation]);

  if (isLoadingModules) return <Spinner className="mx-auto" />;
  if (isErrorModules) return <div>Error</div>;

  if (isLoading) return <div>Loading...</div>;
  if (isError || !pages) return <div>Error loading pages</div>;

  function getTopicComboboxValue(module_id: string) {
    const moduleObj = modules.find((module) => module._id === module_id);
    if (moduleObj) {
      return { value: moduleObj._id, label: moduleObj.title };
    }
    return { value: "", label: "" };
  }

  function getLessonComboboxValue(module_id: string, lesson_id: string) {
    const moduleObj = modules.find((module) => module._id === module_id);
    if (moduleObj) {
      const lessonObj = moduleObj.lessons.find(
        (lesson) => lesson._id === lesson_id,
      );
      if (lessonObj) {
        return { value: lessonObj._id, label: lessonObj.title };
      }
    }
    return { label: "", value: "" };
  }

  function getPageComboboxValue(page_id: string) {
    const pageObj = pages.find((page) => page._id === page_id);
    if (pageObj) {
      return { value: pageObj._id, label: pageObj.title };
    }
    return { label: "", value: "" };
  }

  const handleClearSelection = () => {
    setModuleId(null);
    setLessonId(null);
    setLessonOptions([]);
    setPageOptions(pages);
    setPageId(null);
  };

  const handleOpenFeedbackModal = (
    pageId: string,
    pageIndex: number,
    pageTitle: string,
  ) => {
    setSelectedPageForFeedback({ pageId, pageIndex, pageTitle });
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setSelectedPageForFeedback(null);
  };

  const handleCloseLessonFeedbackModal = () => {
    setIsLessonFeedbackModalOpen(false);
  };

  const pageContent = (
    <>
      <div className="flex flex-row justify-between mb-2 items-center">
        <div className="flex flex-row items-center gap-2">
          <Button
            variant="alternative"
            size="sm"
            onClick={() => setIsLessonFeedbackModalOpen(true)}
            disabled={!lesson._id || !hasLessonFeedback}
            title={lessonFeedbackTitle}
            className={
              !hasLessonFeedback ? "opacity-40 cursor-not-allowed" : ""
            }
          >
            <ChartBarIcon
              className={`w-5 h-5 ${
                !hasLessonFeedback
                  ? "text-gray"
                  : allLessonFeedbackResolved
                    ? "text-electrum-200"
                    : "text-electrum"
              }`}
            />
          </Button>
          <h3 className="m-0">Pages</h3>
        </div>
        <div className="flex space-x-2">
          <Button.Square
            onClick={addNewPage}
            className="p-1.5"
            variant="primary"
          >
            <PlusIcon className="w-5 h-5" />
          </Button.Square>
          <Button.Square
            onClick={handleCopyPage}
            disabled={!pageId || isLoading || isError}
            title="Copy page (creates a duplicate with new ID)"
            className="p-1.5"
            variant="dark"
          >
            <DocumentDuplicateIcon className="w-5 h-5 text-white" />
          </Button.Square>
          <Button.Square
            onClick={handleLinkPage}
            disabled={!pageId || isLoading || isError}
            title="Link page (references the original page)"
            variant="dark"
            className="p-1.5"
          >
            <LinkIcon className="w-5 h-5 text-white" />
          </Button.Square>
          <Button.Square
            onClick={handleClearSelection}
            title="Clear Selection"
            variant="cautionary"
            className="p-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </Button.Square>
        </div>
      </div>
      <div className="mb-2">
        <Combobox
          placeholder="Select a Module"
          options={modules.map((module) => ({
            label: module.title,
            value: module._id,
          }))}
          onChange={(option) => {
            if (option) {
              setModuleId(option.value);
            } else {
              setModuleId("");
            }
            setLessonId("");
            setPageId("");
          }}
          value={getTopicComboboxValue(moduleId)}
          disabled={false}
        />
      </div>
      <div className="mb-2">
        <Combobox
          placeholder="Select a Lesson"
          options={lessonOptions.map((lesson) => ({
            label: lesson.title,
            value: lesson._id,
          }))}
          onChange={(option) => {
            if (option) {
              setLessonId(option.value);
            } else {
              setLessonId("");
              setPageId("");
            }
          }}
          value={getLessonComboboxValue(moduleId, lessonId)}
          disabled={lessonOptions.length === 0}
        />
      </div>
      <div className="mb-2">
        <Combobox
          placeholder="Select a Page"
          options={(pageOptions || pages).map((page) => ({
            label: page.title,
            value: page._id,
          }))}
          onChange={(option) => {
            if (option) {
              setPageId(option.value);
            } else {
              setPageId("");
            }
          }}
          value={getPageComboboxValue(pageId)}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="pages" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {lesson.pages.map((page, index) => (
                  <Draggable
                    key={page._id}
                    draggableId={page._id}
                    index={index}
                  >
                    {(provided) => (
                      <>
                        <PageRow
                          page={page}
                          index={index}
                          lessonId={lesson._id}
                          currentPageId={currentPageId}
                          setCurrentPageId={setCurrentPageId}
                          removePage={removePage}
                          handleOpenFeedbackModal={handleOpenFeedbackModal}
                          provided={provided}
                        />
                        {index < lesson.pages.length - 1 && <hr />}
                      </>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Mobile View */}
      <div className="md:hidden">
        <Card
          minimizable
          initialMinimized
          title={
            <div className="mx-4 flex items-center gap-2">
              <span>Page {currentPageIndex + 1}</span>
              <span className="text-sm text-gray-500">({pageTitle})</span>
            </div>
          }
        >
          {pageContent}
        </Card>
      </div>

      {/* Desktop View */}
      <div className="hidden md:flex md:flex-col md:h-full md:min-h-0">
        {pageContent}
      </div>

      {/* Feedback Modal */}
      {selectedPageForFeedback && lesson._id && (
        <PageFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={handleCloseFeedbackModal}
          lessonId={lesson._id}
          pageNumber={selectedPageForFeedback.pageIndex}
          pageTitle={selectedPageForFeedback.pageTitle}
        />
      )}

      {lesson._id && (
        <LessonFeedbackModal
          isOpen={isLessonFeedbackModalOpen}
          onClose={handleCloseLessonFeedbackModal}
          lessonId={lesson._id}
          lessonTitle={lesson.title}
        />
      )}
    </div>
  );
};

export default PageList;
